import { Gift, Package, Sparkles } from "lucide-react"
import { type ChangeEvent, useId, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

/**
 * OKS-438 — Reward cover image upload control inside the reward Sheet
 * form (`reward-form.tsx`). Replaces the OKS-407 `CoverField` URL input.
 *
 * Mirrors the shipped `BrandCoverImageUploader` idiom (hidden
 * `<input type="file">` inside the parent Inertia `<Form>` so Inertia
 * auto-detects multipart) but with two deliberate deviations (TRD
 * KD-4 / UX-OQ-1):
 *   - **In-form remove, no DELETE route.** Removing stages a hidden
 *     `<input name="reward[remove_cover_image]" value="1">` posted on the
 *     single Sheet Save (rewards are edited in one self-contained form,
 *     unlike the brand cover which has its own profile page + DELETE).
 *   - **Kind-icon fallback tile** (not the brand monogram band) — same
 *     visual vocabulary as the creator `<RewardCover>` (TRD §4).
 *
 * Preview precedence (UX Spec §2): picked file (object URL) → current
 * resolved cover (`currentUrl`, unless removal is staged) → kind-icon
 * fallback tile.
 *
 * Crew form rules: plain shadcn `Input`/`Label`/`Button`/`Dialog`; NO
 * shadcn Form / react-hook-form / zod. Errors render from the parent
 * `<Form>` render-prop `errors.cover_image`.
 */
const KIND_ICON: Record<string, typeof Gift> = {
  product: Package,
  experience: Sparkles,
  custom: Gift,
}

export interface RewardCoverImageFieldProps {
  /** Resolved current cover URL from Inertia props
   *  (`reward.cover_image_url` — attached `:thumb` variant or legacy
   *  string). Null when no cover exists. */
  currentUrl: string | null
  /** True when an Active Storage attachment exists (gates the Remove
   *  affordance — legacy-URL-only rows can't be purged). From
   *  `reward.has_cover_image`. */
  hasCoverImage: boolean
  /** Reward kind — drives the fallback tile glyph. */
  kind: string | null
  /** Validation error from the parent `<Form>` render-prop
   *  (`errors.cover_image`). */
  error?: string | string[]
  /** Wire-format strong-params file input name. */
  inputName?: string
  /** Wire-format remove-flag input name. */
  removeName?: string
  testId?: string
}

export function RewardCoverImageField({
  currentUrl,
  hasCoverImage,
  kind,
  error,
  inputName = "reward[cover_image]",
  removeName = "reward[remove_cover_image]",
  testId = "reward-cover-image-field",
}: RewardCoverImageFieldProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const inputId = useId()
  const helperId = `${inputId}-helper`
  const errorId = `${inputId}-error`

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [removeStaged, setRemoveStaged] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)

  const hasError = Boolean(error)
  const KindIcon = KIND_ICON[kind ?? ""] ?? Gift

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Picking a file always wins over a staged removal (TRD KD-4).
    setRemoveStaged(false)
    setPreviewUrl(file ? URL.createObjectURL(file) : null)
  }

  const onClear = () => {
    setPreviewUrl(null)
    if (inputRef.current) inputRef.current.value = ""
  }

  const onReplaceClick = () => {
    inputRef.current?.click()
  }

  const confirmRemove = () => {
    setRemoveStaged(true)
    onClear()
    setRemoveOpen(false)
  }

  const undoRemove = () => {
    setRemoveStaged(false)
  }

  // The tile shows: a freshly picked file > the current cover (unless
  // removal is staged) > the kind-icon fallback.
  const displayUrl = previewUrl ?? (removeStaged ? null : currentUrl)
  const describedBy =
    [hasError ? errorId : null, helperId].filter(Boolean).join(" ") || undefined

  return (
    <div data-testid={testId} className="grid gap-2">
      <Label htmlFor={inputId}>{t("rewards.form.cover_label")}</Label>

      <div className="flex items-start gap-3">
        {displayUrl ? (
          <img
            src={displayUrl}
            alt={t("rewards.form.cover_preview_alt")}
            data-testid={`${testId}-preview`}
            className="size-20 shrink-0 rounded-lg object-cover"
          />
        ) : (
          <div
            aria-hidden="true"
            data-testid={`${testId}-fallback`}
            className="bg-muted text-muted-foreground flex size-20 shrink-0 items-center justify-center rounded-lg"
          >
            <KindIcon className="size-7" />
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onReplaceClick}
            data-testid={`${testId}-upload`}
            className="min-h-11"
          >
            {hasCoverImage || previewUrl
              ? t("rewards.form.cover_replace")
              : t("rewards.form.cover_upload")}
          </Button>

          {previewUrl ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClear}
              data-testid={`${testId}-clear`}
              className="text-muted-foreground sm:self-center"
            >
              {t("rewards.form.cover_clear")}
            </Button>
          ) : null}

          {hasCoverImage && !previewUrl && !removeStaged ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRemoveOpen(true)}
              data-testid={`${testId}-remove`}
              className="min-h-11 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              {t("rewards.form.cover_remove")}
            </Button>
          ) : null}

          {removeStaged ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={undoRemove}
              data-testid={`${testId}-remove-undo`}
              className="text-muted-foreground sm:self-center"
            >
              {t("rewards.form.cover_remove_undo")}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Hidden file input lives inside the parent Inertia <Form> so the
          submit is auto-detected as multipart. */}
      <Input
        ref={inputRef}
        id={inputId}
        type="file"
        name={inputName}
        accept="image/jpeg,image/png,image/webp"
        onChange={onPick}
        className="sr-only"
        data-testid={`${testId}-input`}
        aria-invalid={hasError}
        aria-describedby={describedBy}
      />

      {/* Staged removal — only rendered (and thus posted) when the brand
          admin confirmed Remove. Absent otherwise, so a plain Save never
          purges. */}
      {removeStaged ? (
        <input
          type="hidden"
          name={removeName}
          value="1"
          data-testid={`${testId}-remove-flag`}
        />
      ) : null}

      <p id={helperId} className={cn("text-muted-foreground text-xs")}>
        {t("rewards.form.cover_helper")}
      </p>

      {hasError ? (
        <p id={errorId} role="alert" className="text-destructive text-sm">
          {Array.isArray(error) ? error[0] : error}
        </p>
      ) : null}

      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent data-testid={`${testId}-remove-dialog`}>
          <DialogTitle>
            {t("rewards.form.cover_remove_confirm_title")}
          </DialogTitle>
          <DialogDescription>
            {t("rewards.form.cover_remove_confirm_body")}
          </DialogDescription>
          <DialogFooter>
            {/* Cancel is primary-focus — destructive action is never the
                  default focus target (UX-OQ-1, mirrors the brand cover
                  remove Dialog). */}
            <DialogClose
              render={
                <Button
                  variant="secondary"
                  autoFocus
                  data-testid={`${testId}-remove-cancel`}
                >
                  {t("rewards.form.cover_remove_confirm_cancel")}
                </Button>
              }
            />
            <Button
              variant="destructive"
              onClick={confirmRemove}
              data-testid={`${testId}-remove-confirm`}
            >
              {t("rewards.form.cover_remove_confirm_cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default RewardCoverImageField
